// src/Pages/DistrictwiseSpecialGSCompliance.jsx
import { useEffect, useState } from "react";
import { Table, Button, Spin, Alert, Space, message, Modal } from "antd";
import { ReloadOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
    getDistrictwiseGSCompliance,
    getBlockwiseGSCompliance,
    getGPwiseGSCompliance
} from "../api/User";

const DistrictwiseSpecialGSComplianceAdmin = ({ dateFilter }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /* -------- Block Modal -------- */
    const [blockModalOpen, setBlockModalOpen] = useState(false);
    const [blockModalTitle, setBlockModalTitle] = useState("");
    const [blockData, setBlockData] = useState([]);
    const [blockLoading, setBlockLoading] = useState(false);

    /* -------- GP Modal -------- */
    const [gpModalOpen, setGpModalOpen] = useState(false);
    const [gpModalTitle, setGpModalTitle] = useState("");
    const [gpData, setGpData] = useState([]);
    const [gpLoading, setGpLoading] = useState(false);


    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    // Block Modal Pagination
    const [blockPage, setBlockPage] = useState(1);
    const [blockPageSize, setBlockPageSize] = useState(10);

    // GP Modal Pagination
    const [gpPage, setGpPage] = useState(1);
    const [gpPageSize, setGpPageSize] = useState(10);


    const fetchReport = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = sessionStorage.getItem("token");
            const res = await getDistrictwiseGSCompliance(token, dateFilter);

            const formatted = res.result.map((row, index) => ({
                key: index + 1,
                sr_no: index + 1,
                ...row,
            }));

            setData(formatted);

        } catch (err) {
            setError(err.message);
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const numberSorter = (a, b, key) => {
        const valA = a[key] || 0;
        const valB = b[key] || 0;
        return valA - valB;
    };

    const stringSorter = (a, b, key) => {
        const valA = (a[key] || '').toString().toLowerCase();
        const valB = (b[key] || '').toString().toLowerCase();
        return valA.localeCompare(valB);
    };


    /* ================= TABLE COLUMNS ================= */
    const columns = [
        {
            title: "Sr. No.",
            dataIndex: "sr_no",
            width: 60,
            fixed: "left",
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
            title: "District",
            dataIndex: "district_name",
            width: 160,
            fixed: "left",
            align: "center",
            sorter: (a, b) => stringSorter(a, b, 'district_name'),
        },
        {
            title: "No. of Blocks",
            dataIndex: "block_count",
            width: 120,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'block_count'),
            render: (val, record) => (
                <span onClick={() => openBlockModal(record)} style={clickStyle}>
                    {val || 0}
                </span>
            ),
        },
        {
            title: "No. of Gram Panchayats",
            dataIndex: "gp_count",
            width: 160,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'gp_count'),
            render: (val, record) => (
                <span onClick={() => openGPFromDistrict(record)} style={clickStyle}>
                    {val || 0}
                </span>
            ),
        },

        /* -------- 1st Meeting -------- */
        {
            title: "1st Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m1_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm1_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m1_updated", align: "center", width: 200, sorter: (a, b) => numberSorter(a, b, 'm1_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m1_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm1_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m1_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm1_quorum_no') },
            ],
        },

        /* -------- 2nd Meeting -------- */
        {
            title: "2nd Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m2_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm2_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m2_updated", align: "center", width: 200, sorter: (a, b) => numberSorter(a, b, 'm2_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m2_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm2_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m2_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm2_quorum_no') },
            ],
        },

        /* -------- 3rd Meeting -------- */
        {
            title: "3rd Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m3_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm3_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m3_updated", align: "center", width: 200, sorter: (a, b) => numberSorter(a, b, 'm3_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m3_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm3_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m3_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm3_quorum_no') },
            ],
        },

        {
            title: "Never Rescheduled (Failed in 1st Meeting)",
            dataIndex: "never_rescheduled",
            align: "center",
            width: 200,
            sorter: (a, b) => numberSorter(a, b, 'never_rescheduled'),
        },
    ];

    /* ================= FETCH DATA ================= */
    useEffect(() => {
        fetchReport();
    }, [dateFilter]);

    /* ================= BLOCK MODAL COLUMNS ================= */
    const blockColumns = [
        {
            title: "Sr. No.",
            dataIndex: "sr_no",
            key: "sr_no",
            width: 60,
            align: "center",
            fixed: "left",
            sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
            title: "District Name",
            dataIndex: "district_name",
            width: 140,
            align: "center",
            fixed: "left",
            sorter: (a, b) => stringSorter(a, b, 'district_name'),
        },
        {
            title: "Block Name",
            dataIndex: "block_name",
            width: 180,
            align: "center",
            fixed: "left",
            sorter: (a, b) => stringSorter(a, b, 'block_name'),
        },
        {
            title: "No. of Gram Panchayats",
            dataIndex: "gp_count",
            width: 150,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'gp_count'),
            render: (value, record) => (
                <span onClick={() => openGPFromBlock(record)} style={clickStyle}>
                    {value || 0}
                </span>
            ),
        },
        /* -------- 1st Meeting -------- */
        {
            title: "1st Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m1_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm1_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m1_updated", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm1_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m1_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm1_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m1_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm1_quorum_no') },
            ],
        },
        /* -------- 2nd Meeting -------- */
        {
            title: "2nd Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m2_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm2_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m2_updated", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm2_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m2_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm2_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m2_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm2_quorum_no') },
            ],
        },
        /* -------- 3rd Meeting -------- */
        {
            title: "3rd Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m3_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm3_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m3_updated", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm3_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m3_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm3_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m3_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm3_quorum_no') },
            ],
        },
        {
            title: "Never Rescheduled (Failed in 1st Meeting)",
            dataIndex: "never_rescheduled",
            align: "center",
            width: 140,
            sorter: (a, b) => numberSorter(a, b, 'never_rescheduled'),
        },
    ];

    /* ================= GP MODAL COLUMNS ================= */
    const gpColumns = [
        {
            title: "Sr. No.",
            dataIndex: "sr_no",
            key: "sr_no",
            width: 60,
            align: "center",
            fixed: "left",
            sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
            title: "District Name",
            dataIndex: "district_name",
            width: 120,
            align: "center",
            fixed: "left",
            sorter: (a, b) => stringSorter(a, b, 'district_name'),
        },
        {
            title: "Block Name",
            dataIndex: "block_name",
            width: 140,
            align: "center",
            fixed: "left",
            sorter: (a, b) => stringSorter(a, b, 'block_name'),
        },
        {
            title: "Gram Panchayat Name",
            dataIndex: "gp_name",
            width: 200,
            align: "center",
            fixed: "left",
            sorter: (a, b) => stringSorter(a, b, 'gp_name'),
        },
        /* -------- 1st Meeting -------- */
        {
            title: "1st Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m1_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm1_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m1_updated", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm1_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m1_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm1_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m1_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm1_quorum_no') },
            ],
        },
        /* -------- 2nd Meeting -------- */
        {
            title: "2nd Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m2_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm2_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m2_updated", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm2_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m2_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm2_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m2_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm2_quorum_no') },
            ],
        },
        /* -------- 3rd Meeting -------- */
        {
            title: "3rd Meeting",
            children: [
                { title: "GPs Conducted", dataIndex: "m3_conducted", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm3_conducted') },
                { title: "GPs Updated (Voters, MOM, Attendance)", dataIndex: "m3_updated", align: "center", width: 120, sorter: (a, b) => numberSorter(a, b, 'm3_updated') },
                { title: "Quorum Fulfilled", dataIndex: "m3_quorum_yes", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm3_quorum_yes') },
                { title: "Quorum Failed", dataIndex: "m3_quorum_no", align: "center", width: 130, sorter: (a, b) => numberSorter(a, b, 'm3_quorum_no') },
            ],
        },
        {
            title: "Never Rescheduled (Failed in 1st Meeting)",
            dataIndex: "never_rescheduled",
            align: "center",
            width: 130,
            sorter: (a, b) => numberSorter(a, b, 'never_rescheduled'),
        },
    ];

    /* ================= MODAL FUNCTIONS ================= */
    const openBlockModal = async (district) => {
        setBlockPage(1);
        setBlockPageSize(10);
        console.log('Opening block modal for district:', district.district_name);
        setBlockModalTitle(`Block-wise Compliance Report : ${district.district_name}`);
        setBlockModalOpen(true);
        setBlockData([]);
        setBlockLoading(true);

        try {
            const token = sessionStorage.getItem("token");

            if (!token) {
                message.error("No authentication token found");
                setBlockLoading(false);
                return;
            }

            // Use the new block-wise compliance API
            const result = await getBlockwiseGSCompliance(token, district.district_code);

            console.log("Block Compliance API Result:", result);

            if (result.success) {
                const blockFormatted = result.result.map((item, index) => ({
                    ...item,
                    key: item.block_code || `block-${index}`,
                    sr_no: index + 1,
                    district_name: item.district_name,
                    block_name: item.block_name,
                    gp_count: item.gp_count || 0,
                    // Compliance data should come directly from API
                    m1_conducted: item.m1_conducted || 0,
                    m1_updated: item.m1_updated || 0,
                    m1_quorum_yes: item.m1_quorum_yes || 0,
                    m1_quorum_no: item.m1_quorum_no || 0,
                    m2_conducted: item.m2_conducted || 0,
                    m2_updated: item.m2_updated || 0,
                    m2_quorum_yes: item.m2_quorum_yes || 0,
                    m2_quorum_no: item.m2_quorum_no || 0,
                    m3_conducted: item.m3_conducted || 0,
                    m3_updated: item.m3_updated || 0,
                    m3_quorum_yes: item.m3_quorum_yes || 0,
                    m3_quorum_no: item.m3_quorum_no || 0,
                    never_rescheduled: item.never_rescheduled || 0,
                    block_code: item.block_code,
                    district_code: item.district_code,
                }));

                setBlockData(blockFormatted);
                message.success(`Loaded ${blockFormatted.length} blocks for ${district.district_name}`);
            } else {
                message.error(result.message || 'Failed to fetch block compliance data');
            }
        } catch (err) {
            console.error('Error fetching block compliance data:', err);
            message.error(err.message || 'Failed to fetch block compliance data');
        } finally {
            setBlockLoading(false);
        }
    };

    const openGPFromDistrict = async (district) => {
        setGpPage(1);
        setGpPageSize(10);
        console.log('Opening GP modal for district:', district.district_name);
        setGpModalTitle(`Gram Panchayat-wise Compliance Report : ${district.district_name}`);
        setGpModalOpen(true);
        setGpData([]);
        setGpLoading(true);

        try {
            const token = sessionStorage.getItem("token");

            if (!token) {
                message.error("No authentication token found");
                setGpLoading(false);
                return;
            }

            // Use the new GP-wise compliance API
            const result = await getGPwiseGSCompliance(token, district.district_code);

            if (result.success) {
                const gpFormatted = result.result.map((item, index) => ({
                    ...item,
                    key: item.panchayat_code || `gp-${index}`,
                    sr_no: index + 1,
                    district_name: item.district_name,
                    block_name: item.block_name,
                    gp_name: item.panchayat_name || item.gp_name,
                    // Compliance data should come directly from API
                    m1_conducted: item.m1_conducted || 0,
                    m1_updated: item.m1_updated || 0,
                    m1_quorum_yes: item.m1_quorum_yes || 0,
                    m1_quorum_no: item.m1_quorum_no || 0,
                    m2_conducted: item.m2_conducted || 0,
                    m2_updated: item.m2_updated || 0,
                    m2_quorum_yes: item.m2_quorum_yes || 0,
                    m2_quorum_no: item.m2_quorum_no || 0,
                    m3_conducted: item.m3_conducted || 0,
                    m3_updated: item.m3_updated || 0,
                    m3_quorum_yes: item.m3_quorum_yes || 0,
                    m3_quorum_no: item.m3_quorum_no || 0,
                    never_rescheduled: item.never_rescheduled || 0,
                    block_code: item.block_code,
                    panchayat_code: item.panchayat_code,
                    district_code: item.district_code,
                }));

                setGpData(gpFormatted);
                message.success(`Loaded ${gpFormatted.length} gram panchayats for ${district.district_name}`);
            } else {
                message.error(result.message || 'Failed to fetch GP compliance data');
            }
        } catch (err) {
            console.error('Error fetching GP compliance data:', err);
            message.error(err.message || 'Failed to fetch GP compliance data');
        } finally {
            setGpLoading(false);
        }
    };

    const openGPFromBlock = async (block) => {
        setGpPage(1);
        setGpPageSize(10);
        setBlockModalOpen(false);
        console.log('Opening GP modal for block:', block.block_name);
        setGpModalTitle(`Gram Panchayat-wise Compliance Report : ${block.block_name} (${block.district_name})`);
        setGpModalOpen(true);
        setGpData([]);
        setGpLoading(true);

        try {
            const token = sessionStorage.getItem("token");

            if (!token) {
                message.error("No authentication token found");
                setGpLoading(false);
                return;
            }

            // Use the new GP-wise compliance API with block filter
            const result = await getGPwiseGSCompliance(token, block.district_code, block.block_code);

            if (result.success) {
                const gpFormatted = result.result.map((item, index) => ({
                    ...item,
                    key: item.panchayat_code || `gp-${index}`,
                    sr_no: index + 1,
                    district_name: item.district_name,
                    block_name: item.block_name,
                    gp_name: item.panchayat_name || item.gp_name,
                    // Compliance data should come directly from API
                    m1_conducted: item.m1_conducted || 0,
                    m1_updated: item.m1_updated || 0,
                    m1_quorum_yes: item.m1_quorum_yes || 0,
                    m1_quorum_no: item.m1_quorum_no || 0,
                    m2_conducted: item.m2_conducted || 0,
                    m2_updated: item.m2_updated || 0,
                    m2_quorum_yes: item.m2_quorum_yes || 0,
                    m2_quorum_no: item.m2_quorum_no || 0,
                    m3_conducted: item.m3_conducted || 0,
                    m3_updated: item.m3_updated || 0,
                    m3_quorum_yes: item.m3_quorum_yes || 0,
                    m3_quorum_no: item.m3_quorum_no || 0,
                    never_rescheduled: item.never_rescheduled || 0,
                    block_code: item.block_code,
                    panchayat_code: item.panchayat_code,
                    district_code: item.district_code,
                }));

                setGpData(gpFormatted);
                message.success(`Loaded ${gpFormatted.length} gram panchayats for ${block.block_name}`);
            } else {
                message.error(result.message || 'Failed to fetch GP compliance data');
            }
        } catch (err) {
            console.error('Error fetching GP compliance data:', err);
            message.error(err.message || 'Failed to fetch GP compliance data');
        } finally {
            setGpLoading(false);
        }
    };




    /* ================= EXPORT FUNCTIONS ================= */
    const handleExportBlockExcel = () => {
        if (!blockData.length) return message.warning("No data to export");

        const excelData = blockData.map((row, idx) => ({
            "Sr. No.": idx + 1,
            "District Name": row.district_name || "-",
            "Block Name": row.block_name || "-",
            "No. of Gram Panchayats": row.gp_count || 0,
            "1st Conducted": row.m1_conducted || 0,
            "1st Updated": row.m1_updated || 0,
            "1st Quorum Yes": row.m1_quorum_yes || 0,
            "1st Quorum No": row.m1_quorum_no || 0,
            "2nd Conducted": row.m2_conducted || 0,
            "2nd Updated": row.m2_updated || 0,
            "2nd Quorum Yes": row.m2_quorum_yes || 0,
            "2nd Quorum No": row.m2_quorum_no || 0,
            "3rd Conducted": row.m3_conducted || 0,
            "3rd Updated": row.m3_updated || 0,
            "3rd Quorum Yes": row.m3_quorum_yes || 0,
            "3rd Quorum No": row.m3_quorum_no || 0,
            "Never Rescheduled (Failed in 1st Meeting)": row.never_rescheduled || 0,
        }));
        excelData.push({
            "Sr. No.": "GRAND TOTAL",
            "District Name": "",
            "Block Name": "",
            "No. of Gram Panchayats": blockTotals.gp_count,

            "1st Conducted": blockTotals.m1_conducted,
            "1st Updated": blockTotals.m1_updated,
            "1st Quorum Yes": blockTotals.m1_quorum_yes,
            "1st Quorum No": blockTotals.m1_quorum_no,

            "2nd Conducted": blockTotals.m2_conducted,
            "2nd Updated": blockTotals.m2_updated,
            "2nd Quorum Yes": blockTotals.m2_quorum_yes,
            "2nd Quorum No": blockTotals.m2_quorum_no,

            "3rd Conducted": blockTotals.m3_conducted,
            "3rd Updated": blockTotals.m3_updated,
            "3rd Quorum Yes": blockTotals.m3_quorum_yes,
            "3rd Quorum No": blockTotals.m3_quorum_no,

            "Never Rescheduled (Failed in 1st Meeting)": blockTotals.never_rescheduled,
        });


        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Block Compliance Report");

        const fileName = `Block_Compliance_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        message.success("Block Excel file exported successfully");
    };

    const handleExportGpExcel = () => {
        if (!gpData.length) return message.warning("No data to export");

        const excelData = gpData.map((row, idx) => ({
            "Sr. No.": idx + 1,
            "District Name": row.district_name || "-",
            "Block Name": row.block_name || "-",
            "Gram Panchayat Name": row.gp_name || "-",
            "1st Conducted": row.m1_conducted || 0,
            "1st Updated": row.m1_updated || 0,
            "1st Quorum Yes": row.m1_quorum_yes || 0,
            "1st Quorum No": row.m1_quorum_no || 0,
            "2nd Conducted": row.m2_conducted || 0,
            "2nd Updated": row.m2_updated || 0,
            "2nd Quorum Yes": row.m2_quorum_yes || 0,
            "2nd Quorum No": row.m2_quorum_no || 0,
            "3rd Conducted": row.m3_conducted || 0,
            "3rd Updated": row.m3_updated || 0,
            "3rd Quorum Yes": row.m3_quorum_yes || 0,
            "3rd Quorum No": row.m3_quorum_no || 0,
            "Never Rescheduled (Failed in 1st Meeting)": row.never_rescheduled || 0,
        }));
        excelData.push({
            "Sr. No.": "GRAND TOTAL",
            "District Name": "",
            "Block Name": "",
            "Gram Panchayat Name": "",

            "1st Conducted": gpTotals.m1_conducted,
            "1st Updated": gpTotals.m1_updated,
            "1st Quorum Yes": gpTotals.m1_quorum_yes,
            "1st Quorum No": gpTotals.m1_quorum_no,

            "2nd Conducted": gpTotals.m2_conducted,
            "2nd Updated": gpTotals.m2_updated,
            "2nd Quorum Yes": gpTotals.m2_quorum_yes,
            "2nd Quorum No": gpTotals.m2_quorum_no,

            "3rd Conducted": gpTotals.m3_conducted,
            "3rd Updated": gpTotals.m3_updated,
            "3rd Quorum Yes": gpTotals.m3_quorum_yes,
            "3rd Quorum No": gpTotals.m3_quorum_no,

            "Never Rescheduled (Failed in 1st Meeting)": gpTotals.never_rescheduled,
        });


        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "GP Compliance Report");

        const fileName = `GP_Compliance_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        message.success("GP Excel file exported successfully");
    };

    /* ================= EXPORT PDF ================= */
    const handleExportPDF = () => {
        const doc = new jsPDF("landscape");
        doc.text("Districtwise Special Gram Sabha Meeting Compliance", 14, 15);

        autoTable(doc, {
            startY: 22,
            head: [columns.flatMap(c => c.children ? c.children.map(sc => sc.title) : c.title)],
            body: [
                ...data.map(row =>
                    columns.flatMap(c =>
                        c.children ? c.children.map(sc => row[sc.dataIndex]) : row[c.dataIndex]
                    )
                ),

                // GRAND TOTAL ROW
                [
                    "GRAND TOTAL",
                    "",
                    districtTotals.block_count,
                    districtTotals.gp_count,

                    districtTotals.m1_conducted,
                    districtTotals.m1_updated,
                    districtTotals.m1_quorum_yes,
                    districtTotals.m1_quorum_no,

                    districtTotals.m2_conducted,
                    districtTotals.m2_updated,
                    districtTotals.m2_quorum_yes,
                    districtTotals.m2_quorum_no,

                    districtTotals.m3_conducted,
                    districtTotals.m3_updated,
                    districtTotals.m3_quorum_yes,
                    districtTotals.m3_quorum_no,

                    districtTotals.never_rescheduled,
                ]
            ],

            styles: { fontSize: 7 },
        });

        doc.save("Districtwise_GS_Compliance.pdf");
    };

    /* ================= EXPORT EXCEL ================= */
    const handleExportExcel = () => {
        const excelData = data.map(row => ({
            District: row.district_name,

            "No. of Blocks": row.block_count || 0,
            "No. of Gram Panchayats": row.gp_count || 0,

            "1st Conducted": row.m1_conducted || 0,
            "1st Updated": row.m1_updated || 0,
            "1st Quorum Yes": row.m1_quorum_yes || 0,
            "1st Quorum No": row.m1_quorum_no || 0,

            "2nd Conducted": row.m2_conducted || 0,
            "2nd Updated": row.m2_updated || 0,
            "2nd Quorum Yes": row.m2_quorum_yes || 0,
            "2nd Quorum No": row.m2_quorum_no || 0,

            "3rd Conducted": row.m3_conducted || 0,
            "3rd Updated": row.m3_updated || 0,
            "3rd Quorum Yes": row.m3_quorum_yes || 0,
            "3rd Quorum No": row.m3_quorum_no || 0,

            "Never Rescheduled (Failed in 1st Meeting)": row.never_rescheduled || 0,
        }));

        // ✅ GRAND TOTAL ROW
        excelData.push({
            District: "GRAND TOTAL",

            "No. of Blocks": districtTotals.block_count,
            "No. of Gram Panchayats": districtTotals.gp_count,

            "1st Conducted": districtTotals.m1_conducted,
            "1st Updated": districtTotals.m1_updated,
            "1st Quorum Yes": districtTotals.m1_quorum_yes,
            "1st Quorum No": districtTotals.m1_quorum_no,

            "2nd Conducted": districtTotals.m2_conducted,
            "2nd Updated": districtTotals.m2_updated,
            "2nd Quorum Yes": districtTotals.m2_quorum_yes,
            "2nd Quorum No": districtTotals.m2_quorum_no,

            "3rd Conducted": districtTotals.m3_conducted,
            "3rd Updated": districtTotals.m3_updated,
            "3rd Quorum Yes": districtTotals.m3_quorum_yes,
            "3rd Quorum No": districtTotals.m3_quorum_no,

            "Never Rescheduled (Failed in 1st Meeting)": districtTotals.never_rescheduled,
        });

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Compliance Report");
        XLSX.writeFile(wb, "Districtwise_GS_Compliance.xlsx");
    };


    /* ================= CLICK STYLE ================= */
    const clickStyle = {
        color: "#1677ff",
        cursor: "pointer",
        textDecoration: "underline",
        fontWeight: 500,
    };
    const districtTotals = {
        block_count: data.reduce((s, i) => s + (i.block_count || 0), 0),
        gp_count: data.reduce((s, i) => s + (i.gp_count || 0), 0),

        m1_conducted: data.reduce((s, i) => s + (i.m1_conducted || 0), 0),
        m1_updated: data.reduce((s, i) => s + (i.m1_updated || 0), 0),
        m1_quorum_yes: data.reduce((s, i) => s + (i.m1_quorum_yes || 0), 0),
        m1_quorum_no: data.reduce((s, i) => s + (i.m1_quorum_no || 0), 0),

        m2_conducted: data.reduce((s, i) => s + (i.m2_conducted || 0), 0),
        m2_updated: data.reduce((s, i) => s + (i.m2_updated || 0), 0),
        m2_quorum_yes: data.reduce((s, i) => s + (i.m2_quorum_yes || 0), 0),
        m2_quorum_no: data.reduce((s, i) => s + (i.m2_quorum_no || 0), 0),

        m3_conducted: data.reduce((s, i) => s + (i.m3_conducted || 0), 0),
        m3_updated: data.reduce((s, i) => s + (i.m3_updated || 0), 0),
        m3_quorum_yes: data.reduce((s, i) => s + (i.m3_quorum_yes || 0), 0),
        m3_quorum_no: data.reduce((s, i) => s + (i.m3_quorum_no || 0), 0),

        never_rescheduled: data.reduce((s, i) => s + (i.never_rescheduled || 0), 0),
    };

    const blockTotals = {
        gp_count: blockData.reduce((s, i) => s + (i.gp_count || 0), 0),

        m1_conducted: blockData.reduce((s, i) => s + (i.m1_conducted || 0), 0),
        m1_updated: blockData.reduce((s, i) => s + (i.m1_updated || 0), 0),
        m1_quorum_yes: blockData.reduce((s, i) => s + (i.m1_quorum_yes || 0), 0),
        m1_quorum_no: blockData.reduce((s, i) => s + (i.m1_quorum_no || 0), 0),

        m2_conducted: blockData.reduce((s, i) => s + (i.m2_conducted || 0), 0),
        m2_updated: blockData.reduce((s, i) => s + (i.m2_updated || 0), 0),
        m2_quorum_yes: blockData.reduce((s, i) => s + (i.m2_quorum_yes || 0), 0),
        m2_quorum_no: blockData.reduce((s, i) => s + (i.m2_quorum_no || 0), 0),

        m3_conducted: blockData.reduce((s, i) => s + (i.m3_conducted || 0), 0),
        m3_updated: blockData.reduce((s, i) => s + (i.m3_updated || 0), 0),
        m3_quorum_yes: blockData.reduce((s, i) => s + (i.m3_quorum_yes || 0), 0),
        m3_quorum_no: blockData.reduce((s, i) => s + (i.m3_quorum_no || 0), 0),

        never_rescheduled: blockData.reduce((s, i) => s + (i.never_rescheduled || 0), 0),
    };
    const gpTotals = {
        m1_conducted: gpData.reduce((s, i) => s + (i.m1_conducted || 0), 0),
        m1_updated: gpData.reduce((s, i) => s + (i.m1_updated || 0), 0),
        m1_quorum_yes: gpData.reduce((s, i) => s + (i.m1_quorum_yes || 0), 0),
        m1_quorum_no: gpData.reduce((s, i) => s + (i.m1_quorum_no || 0), 0),

        m2_conducted: gpData.reduce((s, i) => s + (i.m2_conducted || 0), 0),
        m2_updated: gpData.reduce((s, i) => s + (i.m2_updated || 0), 0),
        m2_quorum_yes: gpData.reduce((s, i) => s + (i.m2_quorum_yes || 0), 0),
        m2_quorum_no: gpData.reduce((s, i) => s + (i.m2_quorum_no || 0), 0),

        m3_conducted: gpData.reduce((s, i) => s + (i.m3_conducted || 0), 0),
        m3_updated: gpData.reduce((s, i) => s + (i.m3_updated || 0), 0),
        m3_quorum_yes: gpData.reduce((s, i) => s + (i.m3_quorum_yes || 0), 0),
        m3_quorum_no: gpData.reduce((s, i) => s + (i.m3_quorum_no || 0), 0),

        never_rescheduled: gpData.reduce((s, i) => s + (i.never_rescheduled || 0), 0),
    };



    return (
        <div className="report-container">
            <div style={{
                background: '#fff',
                padding: '24px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                marginBottom: '24px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            color: '#1f1f1f'
                        }}>
                            Districtwise Special Gram Sabha Meeting Compliance
                        </h2>
                        <p style={{
                            fontSize: '14px',
                            color: '#666',
                            margin: 0
                        }}>
                            Total Records: <strong>{data.length}</strong>
                        </p>
                    </div>

                    <Space>
                        <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>
                            Export PDF
                        </Button>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={handleExportExcel}
                        >
                            Export Excel
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={fetchReport}>
                            Refresh
                        </Button>
                    </Space>
                </div>

                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: '16px' }}
                    />
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="key"
                        bordered
                        size="small"
                        scroll={{ x: 2600, y: 420 }}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: data.length,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            },
                        }}

                    />
                )}
                {data.length > 0 && (
                    <div
                        style={{
                            marginTop: 10,
                            padding: "10px 14px",
                            background: "#f0f9ff",
                            borderRadius: 6,
                            border: "1px solid #bae0ff",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 14,
                            fontSize: 13,
                            flexShrink: 0
                        }}
                    >
                        <span><strong>Grand Totals (All Pages):</strong></span>

                        <span>Blocks: <strong>{districtTotals.block_count}</strong></span>

                        <span>GPs: <strong>{districtTotals.gp_count}</strong></span>

                        <span>1st Conducted: <strong>{districtTotals.m1_conducted}</strong></span>
                        <span>1st Updated: <strong>{districtTotals.m1_updated}</strong></span>
                        <span>1st Quorum Yes: <strong>{districtTotals.m1_quorum_yes}</strong></span>
                        <span>1st Quorum No: <strong>{districtTotals.m1_quorum_no}</strong></span>


                        <span>2nd Conducted: <strong>{districtTotals.m2_conducted}</strong></span>
                        <span>2nd Updated: <strong>{districtTotals.m2_updated}</strong></span>
                        <span>2nd Quorum Yes: <strong>{districtTotals.m2_quorum_yes}</strong></span>
                        <span>2nd Quorum No: <strong>{districtTotals.m2_quorum_no}</strong></span>

                        <span>3rd Conducted: <strong>{districtTotals.m3_conducted}</strong></span>
                        <span>3rd Updated: <strong>{districtTotals.m3_updated}</strong></span>
                        <span>3rd Quorum Yes: <strong>{districtTotals.m3_quorum_yes}</strong></span>
                        <span>3rd Quorum No: <strong>{districtTotals.m3_quorum_no}</strong></span>

                        <span>Never Rescheduled: <strong>{districtTotals.never_rescheduled}</strong></span>
                    </div>
                )}

            </div>


            {/* ========== BLOCK MODAL ========== */}
            <Modal
                open={blockModalOpen}
                onCancel={() => setBlockModalOpen(false)}
                width={1400}
                destroyOnClose
                footer={null}
                closable
                style={{ top: 20 }}
                bodyStyle={{
                    padding: '16px 0 0 0',
                    overflow: 'hidden'
                }}
                title={
                    <div style={{ paddingRight: 40 }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <span style={{ fontSize: 16, fontWeight: 600 }}>
                                {blockModalTitle}
                            </span>

                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={handleExportBlockExcel}
                                disabled={blockLoading || blockData.length === 0}
                            >
                                Export Excel
                            </Button>
                        </div>
                    </div>
                }
            >
                {blockLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 10, color: "#666", fontWeight: 500 }}>
                            Loading block compliance data...
                        </p>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(80vh - 110px)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            paddingRight: '4px'
                        }}>
                            <Table
                                columns={blockColumns}
                                dataSource={blockData}
                                rowKey="key"
                                bordered
                                size="small"
                                scroll={{
                                    x: 1800,
                                    y: undefined
                                }}
                                pagination={{
                                    current: blockPage,
                                    pageSize: blockPageSize,
                                    total: blockData.length,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "20", "50", "100"],
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} blocks`,
                                }}
                                onChange={(pagination) => {
                                    setBlockPage(pagination.current);
                                    setBlockPageSize(pagination.pageSize);
                                }}
                            />
                        </div>

                        {!blockLoading && blockData.length > 0 && (
                            <div
                                style={{
                                    marginTop: 10,
                                    padding: "10px 14px",
                                    background: "#f0f9ff",
                                    borderRadius: 6,
                                    border: "1px solid #bae0ff",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 14,
                                    fontSize: 13,
                                    flexShrink: 0
                                }}
                            >
                                <span><strong>Grand Totals (All Pages):</strong></span>


                                <span>Total GPs: <strong>{blockTotals.gp_count}</strong></span>

                                <span>1st Conducted: <strong>{blockTotals.m1_conducted}</strong></span>
                                <span>1st Updated: <strong>{blockTotals.m1_updated}</strong></span>
                                <span>1st Quorum Yes: <strong>{blockTotals.m1_quorum_yes}</strong></span>
                                <span>1st Quorum No: <strong>{blockTotals.m1_quorum_no}</strong></span>

                                <span>2nd Conducted: <strong>{blockTotals.m2_conducted}</strong></span>
                                <span>2nd Updated: <strong>{blockTotals.m2_updated}</strong></span>
                                <span>2nd Quorum Yes: <strong>{blockTotals.m2_quorum_yes}</strong></span>
                                <span>2nd Quorum No: <strong>{blockTotals.m2_quorum_no}</strong></span>


                                <span>3rd Conducted: <strong>{blockTotals.m3_conducted}</strong></span>
                                <span>3rd Updated: <strong>{blockTotals.m3_updated}</strong></span>
                                <span>3rd Quorum Yes: <strong>{blockTotals.m3_quorum_yes}</strong></span>
                                <span>3rd Quorum No: <strong>{blockTotals.m3_quorum_no}</strong></span>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* ========== GP MODAL ========== */}
            <Modal
                open={gpModalOpen}
                onCancel={() => setGpModalOpen(false)}
                width={1400}
                destroyOnClose
                footer={null}
                closable
                style={{ top: 20 }}
                bodyStyle={{
                    padding: '16px 0 0 0',
                    overflow: 'hidden'
                }}
                title={
                    <div style={{ paddingRight: 40 }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <span style={{ fontSize: 16, fontWeight: 600 }}>
                                {gpModalTitle}
                            </span>

                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={handleExportGpExcel}
                                disabled={gpLoading || gpData.length === 0}
                            >
                                Export Excel
                            </Button>
                        </div>
                    </div>
                }
            >
                {gpLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 10, color: "#666", fontWeight: 500 }}>
                            Loading Gram Panchayat compliance data...
                        </p>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(80vh - 110px)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            paddingRight: '4px'
                        }}>
                            <Table
                                columns={gpColumns}
                                dataSource={gpData}
                                rowKey="key"
                                bordered
                                size="small"
                                scroll={{
                                    x: 1800,
                                    y: undefined
                                }}
                                pagination={{
                                    current: gpPage,
                                    pageSize: gpPageSize,
                                    total: gpData.length,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "20", "50", "100"],
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} GPs`,
                                }}
                                onChange={(pagination) => {
                                    setGpPage(pagination.current);
                                    setGpPageSize(pagination.pageSize);
                                }}
                            />
                        </div>

                        {!gpLoading && gpData.length > 0 && (
                            <div
                                style={{
                                    marginTop: 10,
                                    padding: "10px 14px",
                                    background: "#f0f9ff",
                                    borderRadius: 6,
                                    border: "1px solid #bae0ff",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 14,
                                    fontSize: 13,
                                    flexShrink: 0
                                }}

                            >
                                <span><strong>Grand Totals (All Pages):</strong></span>
                                <span>Gram Panchayats: <strong>{gpData.length}</strong></span>

                                <span>1st Conducted: <strong>{gpTotals.m1_conducted}</strong></span>
                                <span>1st Updated: <strong>{gpTotals.m1_updated}</strong></span>
                                <span>1st Quorum Yes: <strong>{gpTotals.m1_quorum_yes}</strong></span>
                                <span>1st Quorum No: <strong>{gpTotals.m1_quorum_no}</strong></span>

                                <span>2nd Conducted: <strong>{gpTotals.m2_conducted}</strong></span>
                                <span>2nd Updated: <strong>{gpTotals.m2_updated}</strong></span>
                                <span>2nd Quorum Yes: <strong>{gpTotals.m2_quorum_yes}</strong></span>
                                <span>2nd Quorum No: <strong>{gpTotals.m2_quorum_no}</strong></span>

                                <span>3rd Conducted: <strong>{gpTotals.m3_conducted}</strong></span>
                                <span>3rd Updated: <strong>{gpTotals.m3_updated}</strong></span>
                                <span>3rd Quorum Yes: <strong>{gpTotals.m3_quorum_yes}</strong></span>
                                <span>3rd Quorum No: <strong>{gpTotals.m3_quorum_no}</strong></span>

                                <span>Never Rescheduled: <strong>{gpTotals.never_rescheduled}</strong></span>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

        </div>
    );
};

export default DistrictwiseSpecialGSComplianceAdmin;
